/**
 * /check-in/session/{id} — Session Detail page.
 *
 * Mobile-first single scroll, sticky top header, sticky bottom action bar
 * on phones / inline header actions on desktop. Sections are individually
 * collapsible. Data fetched via useCheckInSession (SignalR-aware).
 *
 * Spec: plans/check-in-session-detail.md
 */

import React, { useState } from 'react';
import { Alert, Box, CircularProgress, Container, Stack } from '@mui/material';
import { IconPrinter } from '@tabler/icons-react';
import type {
  CheckInDraftJob,
  CheckInPaymentDeferRequest,
  CheckInContinueWithoutDigitalAcceptanceRequest,
  CheckInSession,
  CheckInTicketGrouping,
} from '@techgitter/contracts';

import '../styles/checkInMobile.css';
import '../styles/checkInSession.css';
import { Chip } from '../components/chip';
import { useCheckInSession } from '../hooks/useCheckInSession';
import {
  CheckInCompletePreconditionError,
  cancelCheckInSession,
  completeCheckInSession,
  markCheckInPaid,
  deferCheckInPayment,
  continueWithoutDigitalAcceptance,
  printCheckInTerms,
  deleteCheckInDraftJob,
  patchCheckInSession,
  recoverCheckInSession,
  createCheckInTickets,
  retryCheckInOrchestration,
} from '../services/checkInApi';
import { sendCheckInToWall } from '../services/wallAdminApi';
import { CheckInSessionHeader } from '../components/check-in/sessionDetail/CheckInSessionHeader';
import { CheckInRetryBanner } from '../components/check-in/sessionDetail/CheckInRetryBanner';
import { CollapsibleSection } from '../components/check-in/sessionDetail/CollapsibleSection';
import { CheckInSessionTokenCard } from '../components/check-in/sessionDetail/CheckInSessionTokenCard';
import { CheckInCustomerDetailsSection } from '../components/check-in/sessionDetail/CheckInCustomerDetailsSection';
import { CheckInDeviceListSection } from '../components/check-in/sessionDetail/CheckInDeviceListSection';
import { CheckInChargesSection } from '../components/check-in/sessionDetail/CheckInChargesSection';
import { TicketGroupingControl } from '../components/check-in/sessionDetail/TicketGroupingControl';
import { CheckInTermsAcceptanceSection } from '../components/check-in/sessionDetail/CheckInTermsAcceptanceSection';
import { CheckInPaymentSection } from '../components/check-in/sessionDetail/CheckInPaymentSection';
import { CheckInPrintActions } from '../components/check-in/print/CheckInPrintActions';
import { CheckInAuditTimeline } from '../components/check-in/sessionDetail/CheckInAuditTimeline';
import {
  CheckInActionBar,
  CheckInDesktopActionGroup,
} from '../components/check-in/sessionDetail/CheckInActionBar';
import { MarkPaidDialog } from '../components/check-in/sessionDetail/dialogs/MarkPaidDialog';
import { DeferPaymentDialog } from '../components/check-in/sessionDetail/dialogs/DeferPaymentDialog';
import { ContinueWithoutDigitalAcceptanceDialog } from '../components/check-in/sessionDetail/dialogs/ContinueWithoutDigitalAcceptanceDialog';
import { CheckInTermsNoticeSection } from '../components/check-in/sessionDetail/CheckInTermsNoticeSection';
import { CancelSessionDialog } from '../components/check-in/sessionDetail/dialogs/CancelSessionDialog';
import { AddDeviceDialog } from '../components/check-in/sessionDetail/dialogs/AddDeviceDialog';
import { RemoveDeviceDialog } from '../components/check-in/sessionDetail/dialogs/RemoveDeviceDialog';
import {
  chargesTotal,
  formatAud,
} from '../components/check-in/sessionDetail/sessionDetailHelpers';
import { deriveSectionProgress } from '../components/check-in/sessionDetail/sectionCompletion';

export interface CheckInSessionDetailPageProps {
  sessionId: string;
  onBack: () => void;
  /** Optional: handler for navigating to the per-device page (Phase 3). */
  onOpenDevice?: (sessionId: string, jobId: string) => void;
  /** Optional: handler for opening the printable check-in slip. */
  onPrintSlip?: (sessionId: string) => void;
}

type ActiveDialog =
  | 'mark-paid' | 'defer' | 'cancel' | 'add-device' | 'remove-device' | 'printed-terms' | null;

export const CheckInSessionDetailPage: React.FC<CheckInSessionDetailPageProps> = ({
  sessionId,
  onBack,
  onOpenDevice,
  onPrintSlip,
}) => {
  const { session, isLoading, error, refresh } = useCheckInSession(sessionId);
  const [optimisticSession, setOptimisticSession] = useState<CheckInSession | null>(null);
  const [activeDialog, setActiveDialog] = useState<ActiveDialog>(null);
  // The device the remove confirmation is about. Held separately from
  // `activeDialog` so the dialog can name it, and so closing clears both.
  const [deviceToRemove, setDeviceToRemove] = useState<
    { job: CheckInDraftJob; label: string } | null
  >(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [isSendingToWall, setIsSendingToWall] = useState(false);
  const [isCreatingTickets, setIsCreatingTickets] = useState(false);
  const [wallNotice, setWallNotice] = useState<string | null>(null);
  // What Defer (New customer) actually reached — a channel that failed, or a
  // ticket left off `Waiting on Payment` — so reception can follow up by hand.
  const [paymentNotice, setPaymentNotice] = useState<{
    severity: 'success' | 'warning';
    message: string;
  } | null>(null);

  // Prefer the optimistic copy when present; otherwise the fetched data.
  // patchCheckInSession returns the full updated entity, so optimistic state
  // stays consistent until the next SignalR-triggered refetch lands.
  const current = optimisticSession ?? session;

  const applyOptimistic = (next: CheckInSession) => {
    setOptimisticSession(next);
    // Discard the optimistic copy once the upstream fetch catches up.
    // useCheckInSession refetches on SignalR; when its `session` updates,
    // the optimistic state is no longer the source of truth.
  };

  React.useEffect(() => {
    // Drop optimistic when the upstream session updates (e.g. SignalR refetch)
    setOptimisticSession(null);
  }, [session?.updatedAt]);

  const handleMarkPaid = async (paymentReference: string) => {
    setActionError(null);
    const updated = await markCheckInPaid(sessionId, paymentReference);
    applyOptimistic(updated);
  };

  const handleContinueWithoutDigitalAcceptance = async (
    payload: CheckInContinueWithoutDigitalAcceptanceRequest,
  ) => {
    setActionError(null);
    const updated = await continueWithoutDigitalAcceptance(sessionId, payload);
    applyOptimistic(updated);
  };

  const handleDefer = async (payload: CheckInPaymentDeferRequest) => {
    setActionError(null);
    setPaymentNotice(null);
    const result = await deferCheckInPayment(sessionId, payload);
    applyOptimistic(result.session);
    if (payload.mode === 'not_required') return;
    const sent = result.deliveries
      .filter(d => d.sent)
      .map(d => `${d.channel === 'sms' ? 'SMS' : 'email'} to ${d.maskedDestination}`);
    const lead = `Invoice sent by ${sent.join(' and ')}.`;
    setPaymentNotice(
      result.warnings.length
        ? { severity: 'warning', message: `${lead} ${result.warnings.join(' ')}` }
        : {
          severity: 'success',
          message: payload.mode === 'new_customer'
            ? `${lead} Ticket set to Waiting on Payment.`
            : lead,
        },
    );
  };

  const handleCancel = async (reason: string) => {
    setActionError(null);
    const updated = await cancelCheckInSession(sessionId, reason);
    applyOptimistic(updated);
  };

  const handleRetry = async () => {
    setActionError(null);
    try {
      await retryCheckInOrchestration(sessionId);
      refresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to retry');
    }
  };

  const handleCreateTickets = async () => {
    setActionError(null);
    setIsCreatingTickets(true);
    try {
      await createCheckInTickets(sessionId);
      // The orchestration runs asynchronously, so the ticket ids land on the
      // draft jobs a moment later. Refresh rather than applying optimistically
      // — there is nothing local to apply.
      refresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to create tickets');
    } finally {
      setIsCreatingTickets(false);
    }
  };

  const handleComplete = async () => {
    setActionError(null);
    try {
      const updated = await completeCheckInSession(sessionId);
      applyOptimistic(updated);
      // Nothing prints here: the paperwork goes out on its own once each
      // ticket's Dataverse job exists (sm4rt-api CheckInAutoPrintService), and
      // the Print section marks itself done from the `document_printed` rows.
    } catch (e) {
      if (e instanceof CheckInCompletePreconditionError) {
        setActionError(
          `${e.unresolvedDraftJobIds.length} device(s) have no RepairShopr `
          + 'ticket yet — press Create Tickets before completing.',
        );
        return;
      }
      setActionError(e instanceof Error ? e.message : 'Failed to complete');
    }
  };

  const [isSavingGrouping, setIsSavingGrouping] = useState(false);
  // Set by the Print section once a document has gone to a printer.
  const [printed, setPrinted] = useState(false);

  const handleTicketGroupingChange = React.useCallback(
    async (grouping: CheckInTicketGrouping) => {
      setActionError(null);
      setIsSavingGrouping(true);
      try {
        // The API refuses this once any device carries a ticket, which is the
        // same rule `canEditTicketGrouping` renders on — so a refusal here
        // means the tickets landed while the page was open, and the refreshed
        // session will render the read-only form.
        applyOptimistic(await patchCheckInSession(sessionId, { ticketGrouping: grouping }));
      } catch (e) {
        setActionError(
          e instanceof Error ? e.message : 'Failed to change the ticket grouping',
        );
        refresh();
      } finally {
        setIsSavingGrouping(false);
      }
    },
    [sessionId, refresh],
  );

  const handleRemoveDevice = React.useCallback(async () => {
    if (!deviceToRemove) return;
    setActionError(null);
    // Errors propagate to the dialog, which keeps itself open and shows them —
    // the server refuses a device that already has a ticket, and that reason is
    // the useful part.
    await deleteCheckInDraftJob(sessionId, deviceToRemove.job.id);
    setDeviceToRemove(null);
    refresh();
  }, [sessionId, deviceToRemove, refresh]);

  const handleRecover = async () => {
    setActionError(null);
    try {
      const updated = await recoverCheckInSession(sessionId);
      applyOptimistic(updated);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to recover');
    }
  };

  /**
   * Put this check-in's QR on the reception TV so the customer can enter their
   * own contact details while staff carry on with the devices.
   *
   * Confirmation matters here: nothing on this page changes, so without a
   * message there is no way to tell from the phone whether it worked — the
   * only feedback is a screen you may not be able to see.
   */
  const handleSendToWall = async () => {
    setActionError(null);
    setWallNotice(null);
    setIsSendingToWall(true);
    try {
      const updated = await sendCheckInToWall(sessionId);
      applyOptimistic(updated);
      setWallNotice('QR code is on the reception TV.');
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to send to the wall');
    } finally {
      setIsSendingToWall(false);
    }
  };

  if (isLoading && !current) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50dvh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !current) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!current) {
    return null;
  }

  // Which steps of this check-in are finished and which still want something.
  // Each panel below carries its own state; the outstanding ones mark
  // themselves and stay open, the finished ones collapse.
  const progress = deriveSectionProgress(current);

  const actionBarProps = {
    session: current,
    onMarkPaid: () => setActiveDialog('mark-paid'),
    onDeferPayment: () => setActiveDialog('defer'),
    onContinueWithoutDigitalAcceptance: () => setActiveDialog('printed-terms'),
    onRetry: handleRetry,
    onComplete: handleComplete,
    onCancel: () => setActiveDialog('cancel'),
    onRecover: handleRecover,
    onSendToWall: handleSendToWall,
    isSendingToWall,
    onCreateTickets: handleCreateTickets,
    isCreatingTickets,
  };

  return (
    <Box className="checkin-page-root" sx={{ display: 'flex', flexDirection: 'column' }}>
      <CheckInSessionHeader
        session={current}
        onBack={onBack}
        desktopActions={<CheckInDesktopActionGroup {...actionBarProps} />}
      />

      <Container maxWidth="md" className="checkin-scroll-area" sx={{ py: 2 }}>
        <CheckInRetryBanner sessionId={sessionId} onRetrySucceeded={refresh} />

        {wallNotice && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setWallNotice(null)}>
            {wallNotice}
          </Alert>
        )}

        {actionError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}

        {onPrintSlip && (
          <div className="ci-print-row">
            <button
              type="button"
              className="ci-btn ci-btn--ghost ci-btn--sm"
              onClick={() => onPrintSlip(sessionId)}
            >
              <IconPrinter size={15} stroke={1.5} />
              Print Check-In Slip
            </button>
          </div>
        )}

        <Stack spacing={1}>
          {/* Rendered bare: the record-trail card carries its own SESSION
              header and footer, so a CollapsibleSection around it showed the
              title twice. */}
          <CheckInSessionTokenCard session={current} />

          <CollapsibleSection title="Customer Details" status={progress.customerDetails}>
            <CheckInCustomerDetailsSection
              session={current}
              onPatched={updated => applyOptimistic(updated)}
            />
          </CollapsibleSection>

          {/* No charges total here — it is the Preliminary Charges section's
              adornment, and saying it twice invited reading them as two
              different figures. */}
          <CollapsibleSection
            title={`Devices (${(current.draftJobs ?? []).length})`}
            status={progress.devices}
          >
            <TicketGroupingControl
              session={current}
              onChange={handleTicketGroupingChange}
              isSaving={isSavingGrouping}
            />
            <CheckInDeviceListSection
              session={current}
              onOpenDevice={jobId => onOpenDevice?.(current.id, jobId)}
              onAddDevice={() => setActiveDialog('add-device')}
              onRemoveDevice={(job, label) => {
                setDeviceToRemove({ job, label });
                setActiveDialog('remove-device');
              }}
            />
          </CollapsibleSection>

          {(current.draftJobs ?? []).some(j => (j.preliminaryCharges ?? []).length > 0) && (
            <CollapsibleSection
              title="Preliminary Charges"
              defaultOpen={false}
              adornment={<Chip tone="neutral">{formatAud(chargesTotal(current))}</Chip>}
            >
              <CheckInChargesSection session={current} />
            </CollapsibleSection>
          )}

          {/*
            Rendered while the terms are still outstanding as well as after they
            are accepted. The panel used to appear only once `termsAcceptance`
            existed, which made the one step the customer owns the one step
            nobody could see was pending.
          */}
          {(current.termsAcceptance || current.termsNotice || progress.terms === 'todo') && (
            <CollapsibleSection title="Terms Acceptance" status={progress.terms}>
              {current.termsAcceptance ? (
                <CheckInTermsAcceptanceSection session={current} />
              ) : current.termsNotice ? (
                <CheckInTermsNoticeSection notice={current.termsNotice} />
              ) : (
                <div className="ci-awaiting">
                  Not accepted yet — the customer accepts the terms on their own
                  phone, or on the reception TV once the QR is up.
                  {current.continueWithoutDigitalAcceptance?.available && (
                    <> If they can&apos;t, use <strong>Continue without digital
                    acceptance</strong> to print the terms and hand them over.</>
                  )}
                </div>
              )}
            </CollapsibleSection>
          )}

          {/* Hidden when no payment was ever due (express, returns start at
              `not_required`). Not Required chosen by reception keeps the
              panel, so it can say who chose it. */}
          {(current.paymentState !== 'not_required' || !!current.paymentOverriddenBy) && (
            <CollapsibleSection title="Payment" status={progress.payment}>
              {paymentNotice && (
                <Alert
                  severity={paymentNotice.severity}
                  onClose={() => setPaymentNotice(null)}
                  sx={{ mb: 1 }}
                >
                  {paymentNotice.message}
                </Alert>
              )}
              <CheckInPaymentSection
                session={current}
                onMarkPaid={() => setActiveDialog('mark-paid')}
                onDeferPayment={() => setActiveDialog('defer')}
              />
            </CollapsibleSection>
          )}

          {/*
            Marked done once something has actually printed. `null` until then
            rather than 'todo': printing is not a step of every check-in — the
            AutoPrint binding has usually already sent the paperwork — so an
            amber "To do" on every session would be claiming work that is not
            owed.
            `progress.print` reads the session's `document_printed` audit rows,
            so the mark survives a refresh; `printed` is the local echo that
            shows it immediately, before the next fetch brings the row back.
          */}
          <CollapsibleSection
            title="Print"
            defaultOpen={false}
            status={printed ? 'done' : progress.print}
          >
            <CheckInPrintActions
              session={current}
              devices={current.draftJobs ?? []}
              onPrinted={() => {
                setPrinted(true);
                // Bring the `document_printed` audit row back, so the mark is
                // the server's answer rather than this page's memory of it.
                refresh();
              }}
            />
          </CollapsibleSection>

          <CollapsibleSection
            title={`Audit (${(current.audit ?? []).length})`}
            defaultOpen={!!current.requiresAttention}
          >
            <CheckInAuditTimeline entries={current.audit ?? []} />
          </CollapsibleSection>
        </Stack>
      </Container>

      <CheckInActionBar {...actionBarProps} />

      <AddDeviceDialog
        open={activeDialog === 'add-device'}
        sessionId={sessionId}
        deviceIndex={(current.draftJobs ?? []).length}
        intakeType={current.intakeType ?? null}
        originalWorkItemId={current.originalWorkItemId ?? null}
        ticketGrouping={current.ticketGrouping ?? null}
        // Snapshot from the session, same suppression rule the create page
        // applies: Core / Core+ are billed monthly, so no diagnostic line.
        isCoreCustomer={
          current.customerDesignation === 'Core' || current.customerDesignation === 'Core+'
        }
        onClose={() => setActiveDialog(null)}
        onAdded={() => refresh()}
      />

      <RemoveDeviceDialog
        open={activeDialog === 'remove-device' && !!deviceToRemove}
        label={deviceToRemove?.label ?? ''}
        chargesTotal={(deviceToRemove?.job.preliminaryCharges ?? []).reduce(
          (sum, c) => sum + (c.finalTotal || 0), 0,
        )}
        onClose={() => {
          setActiveDialog(null);
          setDeviceToRemove(null);
        }}
        onConfirm={handleRemoveDevice}
      />

      <MarkPaidDialog
        open={activeDialog === 'mark-paid'}
        onClose={() => setActiveDialog(null)}
        onConfirm={handleMarkPaid}
      />
      <DeferPaymentDialog
        open={activeDialog === 'defer'}
        session={current}
        onClose={() => setActiveDialog(null)}
        onConfirm={handleDefer}
      />
      <ContinueWithoutDigitalAcceptanceDialog
        open={activeDialog === 'printed-terms'}
        onClose={() => setActiveDialog(null)}
        onPrint={target => printCheckInTerms(sessionId, target)}
        onConfirm={handleContinueWithoutDigitalAcceptance}
      />
      <CancelSessionDialog
        open={activeDialog === 'cancel'}
        onClose={() => setActiveDialog(null)}
        onConfirm={handleCancel}
      />
    </Box>
  );
};
